import React, { useState } from 'react';

import Axios from 'axios';

import styled from 'styled-components';

import { isJobEvent } from '@libs/EventTypes';

import { useAddModelToDate, useAddModelToDates, useDeleteEventDatesModels } from '@hooks/API/Event/UseEvent';

import { useContactsSelectorDispatch } from '@components/ContactsSelector/Common/ContactsSelectorContext';
import CalendarIconDate from '@components/Dates/CalendarIconDate';
import OptionNumber from '@components/EventDrawer/EventDrawerUi/OptionNumber';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const OptionNumberContainer = styled.div`
    position: absolute;
    top: 0.25rem;
    right: 0.5rem;
`;

const CheckIconContainer = styled.div`
    position: absolute;
    top: 0.25rem;
    right: 0.5rem;

    svg {
        font-size: 1.5rem;
        --fa-primary-color: white;
        --fa-secondary-color: ${(props) => props.theme.SuccessColor};
        --fa-primary-opacity: 1;
        --fa-secondary-opacity: 1;
        cursor: pointer;
    }
`;

const VerticalDateButton = styled.div`
    cursor: pointer;
    padding-right: 1rem;
    position: relative;
`;

const OptionNumberButton = ({ currentEvent, eventDate, modelID, handleUpdateEvent }) => {
    if (isJobEvent(currentEvent.TypeName)) {
        const eventdatemodel = eventDate.EventDatesModels.find((edm) => edm.ModelID === modelID);
        if (!eventdatemodel) {
            return null;
        }
        return (
            <OptionNumberContainer onClick={(e) => e.stopPropagation()}>
                <OptionNumber
                    key={eventdatemodel.OptionNumber}
                    number={eventdatemodel.OptionNumber}
                    event={currentEvent}
                    eventDates={currentEvent.eventDates}
                    eventDateInfo={eventDate}
                    selectedModel={eventdatemodel}
                    eventDatesModel={eventdatemodel}
                    reloadGroupedModels={() => {}}
                    onExtendOptionNumberToAllDates={() => {}}
                    onChangeOptionNumber={handleUpdateEvent}
                />
            </OptionNumberContainer>
        );
    } else {
        return (
            <CheckIconContainer>
                <NwIcon icon={byPrefixAndName.fasds['circle-check']} />
            </CheckIconContainer>
        );
    }
};

const ModelsTableColumnEventDate = ({
    addModelsAs,
    availability,
    availabilityInfo,
    eventDate,
    model,
    currentEvent,
}) => {
    const { mutateAsync: addModelToDate } = useAddModelToDate();
    const { mutateAsync: addModelToDates } = useAddModelToDates();
    const { mutateAsync: removeModelFromDate } = useDeleteEventDatesModels();

    const [addingModel, setAddingModel] = useState(false);
    const [addingModelToAllDates, setAddingModelToAllDates] = useState(false);
    const [removingModel, setRemovingModel] = useState(false);

    const { eventDatesIncluded, isFetching } = availabilityInfo;

    const cs_dispatch = useContactsSelectorDispatch();

    const datesToAdd = currentEvent.EventDates.filter((ed) =>
        ed.EventDatesModels.every((edm) => Number(edm.ModelID) !== Number(model.ID))
    ).map((ed) => ed.ID);

    const handleUpdateEvent = async () => {
        const eventUpdated = await Axios.get(`/events/${currentEvent.ID}`);
        cs_dispatch({ type: 'UpdateCurrentEvent', eventUpdated: eventUpdated });
    };

    const handleModelAdd = async () => {
        setAddingModel(true);
        try {
            await addModelToDate({
                modelId: model.ID,
                eventDateId: eventDate.ID,
                eventId: currentEvent.ID,
                optionNumber: addModelsAs,
            });
            await handleUpdateEvent();
            setAddingModel(false);
        } catch (error) {
            console.error('Add Models to EventDate Error => ', error);
            setAddingModel(false);
        }
    };

    const handleModelRemove = async (eventDate) => {
        const eventDateModel = eventDate.EventDatesModels.find((edm) => edm.ModelID === model.ID);
        if (!eventDateModel) {
            return;
        }
        setRemovingModel(true);
        try {
            await removeModelFromDate({
                eventDateModelId: eventDateModel.ID,
                ID: currentEvent.ID,
                projectID: currentEvent.Project.ID,
            });
            await handleUpdateEvent();
            setRemovingModel(false);
        } catch (error) {
            console.error('Remove Model from EventDate Error => ', error);
            setRemovingModel(false);
        }
    };

    const handleModelAddAllDates = async () => {
        setAddingModelToAllDates(true);
        try {
            await addModelToDates({
                modelId: model.ID,
                eventDatesIds: [...datesToAdd],
                eventId: currentEvent.ID,
                optionNumber: addModelsAs,
            });
            await handleUpdateEvent();
            setAddingModelToAllDates(false);
        } catch (error) {
            console.error('Add Models to EventDate Error => ', error);
            setAddingModelToAllDates(false);
        }
    };

    if (eventDate) {
        //single-date event
        return isFetching ? (
            <CalendarIconDate loading />
        ) : availability.inEvent > 0 ? (
            <VerticalDateButton available={false} inEvent={true} onClick={() => handleModelRemove(eventDate)}>
                <CalendarIconDate date={eventDate.FromDate} color={StyleVariables.PrimaryColor} removable />
                <OptionNumberButton
                    currentEvent={currentEvent}
                    eventDate={eventDate}
                    modelID={model.ID}
                    handleUpdateEvent={handleUpdateEvent}
                />
            </VerticalDateButton>
        ) : !availability.isBusy ? (
            <VerticalDateButton available={true} inEvent={false} onClick={() => handleModelAdd(eventDate.ID)}>
                <CalendarIconDate
                    loading={addingModel || removingModel}
                    date={eventDate.FromDate}
                    color={!eventDatesIncluded ? StyleVariables.DarkerBorderColor : StyleVariables.SuccessColor}
                    addible
                />
            </VerticalDateButton>
        ) : (
            <VerticalDateButton
                available={availability.available}
                inEvent={availability.inEvent}
                onClick={() => handleModelAdd(eventDate.ID)}
            >
                <CalendarIconDate
                    loading={addingModel || removingModel}
                    date={eventDate.FromDate}
                    color={!eventDatesIncluded ? StyleVariables.DarkerBorderColor : StyleVariables.DangerColor}
                    addible
                />
            </VerticalDateButton>
        );
    } else {
        return (
            <VerticalDateButton available={true} inEvent={false} onClick={() => handleModelAddAllDates()}>
                <CalendarIconDate loading={addingModelToAllDates} color={StyleVariables.DarkerBorderColor} />
            </VerticalDateButton>
        );
    }
};

export default ModelsTableColumnEventDate;
